/**
 * The project Home as gadgets (design "Flow Project Redesign", Home board): each block of the Home is a gadget the
 * person can reorder, resize (S / M / L), hide and bring back, kept per person in this browser (`flow.homeGadgets`),
 * the same on every project. A gadget the build does not know (an older or newer saved list) is dropped; a new one
 * is added at the end with its default size, so an upgrade never loses a block.
 *
 * A gadget can be hidden by default (`hidden: true` in the catalog): it starts in "+ Add gadget", not on the board.
 * That default applies only to a gadget the stored preferences do not mention at all (a new one, after an upgrade), so a
 * board someone already arranged keeps its layout, and a gadget they added or hid stays as they left it.
 *
 * A gadget can keep its own settings (`settings[id]`, a plain object: the File gadget's chosen file per project), saved
 * with the layout and dropped with it on Reset.
 *
 * `createGadgetsModel` builds the same machinery for any catalog and storage key: the workspace page's Overview
 * (`flow.workspaceGadgets`, utils/workspaceGadgets.js) uses it too, and HomeGadgets.jsx draws either.
 */
export const SIZES = ['S', 'M', 'L'];
// Columns of a 3-column grid each size takes.
export const SIZE_SPAN = { S: 1, M: 2, L: 3 };

/**
 * The preferences functions for `gadgets` (`[{ id, label, desc, size, preview?, hidden? }]`, preview: the Add gadget
 * thumbnail, hidden: not on the board until added) kept under `key`, announced by `event`.
 */
export function createGadgetsModel({ gadgets, key, event }) {
  const ids = gadgets.map((gadget) => gadget.id);
  const hiddenByDefault = gadgets.filter((gadget) => gadget.hidden).map((gadget) => gadget.id);
  const defaults = () => ({
    order: ids.slice(),
    sizes: Object.fromEntries(gadgets.map((gadget) => [gadget.id, gadget.size])),
    hidden: hiddenByDefault.slice(),
    // Gadgets two rows high (a tall one beside two stacked ones).
    tall: [],
    // Each gadget's own settings, by id (a plain object; none: {}).
    settings: {},
  });
  const known = (id) => ids.includes(id);

  /**
   * Any stored value → full preferences: known gadgets only, new ones at the end, wrong sizes back to the default. A
   * gadget hidden by default that the stored value does not mention (in its order or its hidden list) is hidden.
   */
  function normalizeGadgetPrefs(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const base = defaults();
    const saved = Array.isArray(value.order) ? value.order.filter((id, i, all) => known(id) && all.indexOf(id) === i) : [];
    const order = [...saved, ...ids.filter((id) => !saved.includes(id))];
    const sizes = Object.fromEntries(ids.map((id) => [id, SIZES.includes(value.sizes?.[id]) ? value.sizes[id] : base.sizes[id]]));
    const storedHidden = Array.isArray(value.hidden) ? value.hidden.filter((id, i, all) => known(id) && all.indexOf(id) === i) : [];
    const mentioned = new Set([...(Array.isArray(value.order) ? value.order : []), ...storedHidden]);
    const hidden = [...storedHidden, ...hiddenByDefault.filter((id) => !mentioned.has(id))];
    const tall = Array.isArray(value.tall) ? value.tall.filter((id, i, all) => known(id) && all.indexOf(id) === i) : [];
    const isPlain = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
    const settings = isPlain(value.settings)
      ? Object.fromEntries(Object.entries(value.settings).filter(([id, v]) => known(id) && isPlain(v)))
      : {};
    return { order, sizes, hidden, tall, settings };
  }

  function readGadgetPrefs() {
    try {
      return normalizeGadgetPrefs(JSON.parse(localStorage.getItem(key) || 'null'));
    } catch {
      return normalizeGadgetPrefs(null);
    }
  }

  function saveGadgetPrefs(prefs) {
    const next = normalizeGadgetPrefs(prefs);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* kept for this page only */ }
    if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent(event, { detail: next })); }
    return next;
  }

  function resetGadgetPrefs() {
    try { localStorage.removeItem(key); } catch { /* nothing kept */ }
    return saveGadgetPrefs(null);
  }

  /** The visible gadgets in order, with their size. */
  const visibleGadgets = (prefs) => prefs.order.filter((id) => !prefs.hidden.includes(id))
    .map((id) => ({ ...gadgets.find((g) => g.id === id), size: prefs.sizes[id], tall: (prefs.tall || []).includes(id) }));

  /** The hidden gadgets, for "Add gadget". */
  const hiddenGadgets = (prefs) => gadgets.filter((gadget) => prefs.hidden.includes(gadget.id));

  /** Moves a visible gadget one place up (-1) or down (+1) among the visible ones; the hidden ones keep their slots. */
  function moveGadget(prefs, id, delta) {
    const shown = prefs.order.filter((g) => !prefs.hidden.includes(g));
    const from = shown.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= shown.length) { return prefs; }
    const next = shown.slice();
    [next[from], next[to]] = [next[to], next[from]];
    let i = 0;
    return { ...prefs, order: prefs.order.map((g) => (prefs.hidden.includes(g) ? g : next[i++])) };
  }

  /** Drops the visible gadget `id` on the visible gadget `overId`: it takes that place, the ones between shift by one. */
  function moveGadgetTo(prefs, id, overId) {
    const shown = prefs.order.filter((g) => !prefs.hidden.includes(g));
    const from = shown.indexOf(id);
    const to = shown.indexOf(overId);
    if (from < 0 || to < 0 || from === to) { return prefs; }
    const next = shown.slice();
    next.splice(from, 1);
    next.splice(to, 0, id);
    let i = 0;
    return { ...prefs, order: prefs.order.map((g) => (prefs.hidden.includes(g) ? g : next[i++])) };
  }

  const withSize = (prefs, id, size) => (SIZES.includes(size) ? { ...prefs, sizes: { ...prefs.sizes, [id]: size } } : prefs);
  const withHidden = (prefs, id) => (prefs.hidden.includes(id) ? prefs : { ...prefs, hidden: [...prefs.hidden, id] });
  const withShown = (prefs, id) => ({ ...prefs, hidden: prefs.hidden.filter((g) => g !== id) });
  const withTall = (prefs, id, on) => {
    const tall = (prefs.tall || []).filter((g) => g !== id);
    return { ...prefs, tall: on ? [...tall, id] : tall };
  };

  /** `id`'s own settings ({} when it has none). */
  const settingsOf = (prefs, id) => prefs?.settings?.[id] || {};
  /** `id`'s settings replaced by `value` (a plain object). */
  const withSettings = (prefs, id, value) => ({ ...prefs, settings: { ...(prefs.settings || {}), [id]: value } });

  return {
    GADGETS: gadgets,
    GADGET_IDS: ids,
    GADGETS_EVENT: event,
    DEFAULT_GADGET_PREFS: Object.freeze(defaults()),
    normalizeGadgetPrefs,
    readGadgetPrefs,
    saveGadgetPrefs,
    resetGadgetPrefs,
    visibleGadgets,
    hiddenGadgets,
    moveGadget,
    moveGadgetTo,
    withSize,
    withHidden,
    withShown,
    withTall,
    settingsOf,
    withSettings,
  };
}

const HOME_GADGETS = [
  { id: 'apps', label: 'Apps', desc: 'Each service: state, CPU, memory, Open, Stop, Restart, Logs.', size: 'M', preview: 'status' },
  { id: 'attention', label: 'Needs attention', desc: 'What is failing or stopped, with a way to fix it.', size: 'S', preview: 'list' },
  { id: 'changed', label: 'What changed', desc: 'The latest activity since you left.', size: 'S', preview: 'list' },
  { id: 'files', label: 'Files', desc: 'Drop files, and the latest inputs and results.', size: 'S', preview: 'drop' },
  { id: 'brain', label: 'Brain', desc: 'The project\'s notes: docs, memory, scripts.', size: 'S', preview: 'folders' },
  { id: 'ready', label: 'Ready to publish?', desc: 'What the release gate checks, and what blocks Publish.', size: 'S', preview: 'progress' },
  { id: 'requests', label: 'Requests', desc: 'How many requests the services answered and how many failed, from Traces.', size: 'S', preview: 'bars' },
  { id: 'plan', label: 'Plan and usage', desc: 'Your plan and how much of your AI credits is used this month.', size: 'S', preview: 'number' },
  { id: 'more', label: 'More about this project', desc: 'How it fits together, Brain, AI used, Conversations.', size: 'L', preview: 'list' },
  // Not on the board until someone adds them (+ Add gadget), grouped as the workspace's tabs are.
  // Build
  { id: 'preview', label: 'Preview', desc: 'A live thumbnail of the main service, with Open and Restart.', size: 'M', preview: 'frame', hidden: true },
  { id: 'code', label: 'Code activity', desc: 'Commits and changed files per service in the last 7 days, and what is not committed.', size: 'S', preview: 'bars', hidden: true },
  { id: 'deps', label: 'Dependencies', desc: 'Outdated and vulnerable packages per service, from Quality.', size: 'S', preview: 'table', hidden: true },
  { id: 'agents', label: 'Agents at work', desc: 'The AI agents working in the project now, and on which file.', size: 'S', preview: 'avatars', hidden: true },
  { id: 'file', label: 'File', desc: 'One file of the project on Home: a Markdown note rendered, or an image.', size: 'M', preview: 'doc', hidden: true },
  { id: 'todo', label: 'Open work', desc: 'Failing checks, secrets to set and TODO / FIXME notes in the code.', size: 'S', preview: 'list', hidden: true },
  // Verify
  { id: 'tests', label: 'Tests', desc: 'The last run per service, its coverage, and how the last runs went.', size: 'S', preview: 'trend', hidden: true },
  { id: 'errors', label: 'Errors', desc: 'Failed requests from Traces and services that crashed, each with its trace or log.', size: 'S', preview: 'status', hidden: true },
  { id: 'apihealth', label: 'API health', desc: 'How many endpoints the services have, and the slowest services by p95.', size: 'S', preview: 'table', hidden: true },
  { id: 'security', label: 'Security', desc: 'Critical and high vulnerabilities, live secrets and licence issues, from Quality.', size: 'S', preview: 'tiles', hidden: true },
  // Ship
  { id: 'release', label: 'Release', desc: 'What changed since the last Publish, with Publish.', size: 'S', preview: 'progress', hidden: true },
  { id: 'envs', label: 'Environments', desc: 'Which version runs where, from the deploys Console Next reported.', size: 'S', preview: 'stages', hidden: true },
  { id: 'confighealth', label: 'Configuration health', desc: 'Secrets to set, variables only some services have, paused jobs.', size: 'S', preview: 'status', hidden: true },
  // Operate
  { id: 'uptime', label: 'Uptime and resources', desc: 'Per service: up since, restarts, CPU and memory now.', size: 'M', preview: 'table', hidden: true },
  { id: 'jobs', label: 'Jobs', desc: 'Next and last run of each scheduled job, failures, Run now.', size: 'S', preview: 'timeline', hidden: true },
  { id: 'data', label: 'Data', desc: 'The project\'s datastores, their tables and how many records.', size: 'S', preview: 'table', hidden: true },
  { id: 'cost', label: 'Cost', desc: 'Your credits this month and an estimate of the app in production.', size: 'S', preview: 'number', hidden: true },
  { id: 'backups', label: 'Backups', desc: 'The last backup and the restore points of the data.', size: 'S', preview: 'timeline', hidden: true },
  // Team
  { id: 'people', label: 'Who\'s here', desc: 'People and agents here now, and your unread messages.', size: 'S', preview: 'avatars', hidden: true },
  { id: 'reviews', label: 'Reviews and comments', desc: 'Open comment threads, suggestions waiting and handoff notes for you.', size: 'S', preview: 'thread', hidden: true },
];

export const homeGadgetsModel = createGadgetsModel({ gadgets: HOME_GADGETS, key: 'flow.homeGadgets', event: 'flow:home-gadgets-changed' });

export const {
  GADGETS, GADGET_IDS, GADGETS_EVENT, DEFAULT_GADGET_PREFS, normalizeGadgetPrefs, readGadgetPrefs, saveGadgetPrefs, resetGadgetPrefs,
  visibleGadgets, hiddenGadgets, moveGadget, moveGadgetTo, withSize, withHidden, withShown, withTall, settingsOf, withSettings,
} = homeGadgetsModel;
